import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { DashboardSectionHeader } from "src/components/common";
import {
  buildSocialReport,
  type OrganicSocialPlatform,
} from "../../../data/organicSocialMockData";
import OrganicDateRangePicker from "../OrganicDateRangePicker";
import type { OrganicDateFilters } from "../FacebookContentReport";
import SocialReportKpiCards from "./SocialReportKpiCards";
import SocialReportBreakdownRow from "./SocialReportBreakdownRow";
import SocialReportTrendsPanel from "./SocialReportTrendsPanel";
import SocialReportContentTable from "./SocialReportContentTable";
import { SOCIAL_PLATFORM_LABEL_KEY, SOCIAL_PLATFORM_TAB_KEY } from "./socialReportConfig";
import SocialReportAiSummary from "./SocialReportAiSummary";
import "./SocialContentFollowersReport.scss";

interface SocialContentFollowersReportProps {
  platform: OrganicSocialPlatform;
  dateFilters: OrganicDateFilters;
}

const SocialContentFollowersReport: FC<SocialContentFollowersReportProps> = ({
  platform,
  dateFilters,
}) => {
  const { t } = useTranslation();

  const platformLabel = t(SOCIAL_PLATFORM_LABEL_KEY[platform]);

  const report = useMemo(
    () =>
      buildSocialReport(platform, dateFilters.appliedStartDate, dateFilters.appliedEndDate),
    [platform, dateFilters.appliedStartDate, dateFilters.appliedEndDate]
  );

  return (
    <div className="organic-content-report">
      <DashboardSectionHeader
        className="organic-content-report__section-header"
        title={t(SOCIAL_PLATFORM_TAB_KEY[platform])}
        subtitle={t("organicMedia.social.subtitle", { platform: platformLabel })}
        actionsClassName="dashboard-section-header__actions--filters"
        actions={
          <div className="dashboard-section-header__control dashboard-section-header__control--wide">
            <OrganicDateRangePicker
              startDate={dateFilters.draftStartDate}
              endDate={dateFilters.draftEndDate}
              onRangeChange={dateFilters.onApplyDates}
            />
          </div>
        }
      />

      <SocialReportAiSummary
        platform={platform}
        report={report}
        startDate={dateFilters.appliedStartDate}
        endDate={dateFilters.appliedEndDate}
      />

      <SocialReportKpiCards
        platform={platform}
        totals={report.totals}
        priorTotals={report.priorTotals}
      />

      <SocialReportBreakdownRow
        typePerformance={report.typePerformance}
        trendsPanel={<SocialReportTrendsPanel daily={report.daily} />}
      />

      <SocialReportContentTable
        platform={platform}
        rows={report.content}
        csvFilename={`${platform}-content-performance.csv`}
      />
    </div>
  );
};

export default SocialContentFollowersReport;
